/**
 * Implementation of IFloodgateStringProvider for Web SDK
 */

import {
	Api,
	BranchingRule,
	BranchingTargetOverride,
	BranchingTargetType,
	IUxSchema,
	IUxSchemaPage,
	OverridePropertiesType,
	Question,
	QuestionType
} from "@ms-ofb/floodgate-shared-typings";
import { isNOU } from "../Utils";
import { HttpTransport } from "../Transport/HttpTransport";
import { TelemetryEvent } from "../Constants";
import { TelemetryLogger } from "../TelemetryLogger";

interface UiStringsMap {
	[key:string]: string
}

export function isTranslationRequired(survey: Api.ISurvey, locale: string): boolean {
	const currentLocale = locale?.toLowerCase() || 'en-us';
	return survey.getUxSchema()?.uiStringsUrl && 
		(currentLocale != 'en-us' && currentLocale != 'en');
}

/**
 * This class is the client to query the CDN from the given survey UxSchema if required
 * The survey UxSchema will be translated into the given locale
 */
export default class FloodgateSurveyCdnClient {
	public timeout: number;
	public retryLimit: number;
	public simpleRequestEnabled: boolean;
	
	constructor(uiStringsLoadingTimeout: number, retryLimit: number, simpleRequestEnabled = false) {
		this.timeout = uiStringsLoadingTimeout;
		this.retryLimit = retryLimit;
		this.simpleRequestEnabled = simpleRequestEnabled;
	}

	/**
	 * The function to translate the given survey UxSchema into the given locale
	 * If the CDN is failed or UxSchema is failed in translation, the UxSchema will be reset to null
	 * In case of failure, it can possible returns success if the legacy Content is valid.
	 * @returns true if success. Otherwise, false.
	 */
	public async loadCdnStringResource(survey: Api.ISurvey, locale: string): Promise<boolean> {
		const requestUrl: string = survey.getUxSchema().uiStringsUrl + locale + "/strings.json";
		const httpTransport = new HttpTransport();
		let success = false;
		let response: Response;
		let serviceCallTime: number;

		// Survey CDN is not enabled, assume it's good
		if (!isTranslationRequired(survey, locale)) {
			return true;
		}

		try {
			const timeBefore = performance.now();
			response = await httpTransport.getRequestWithRetry(requestUrl, this.retryLimit, this.timeout, this.simpleRequestEnabled);
			serviceCallTime = performance.now() - timeBefore;

			if (response.ok) {
				// Get the response text
				const responseText = await response.text();

				// Log the successful post criteria with time
				TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateSurveyCdnClient.loadCdnStringResource.Request_Success,
					{
						TimeMilliseconds: serviceCallTime,
						CampaignId: survey.getCampaignId(),
						SurveyId: survey.getSurveyInfo().getId(),
						Message: JSON.stringify({ 
							RequestUrl: requestUrl,
							HttpStatusCode: response.status,
							HttpStatusText: response.statusText,
							SimpleRequestEnabled: this.simpleRequestEnabled,
						}),
					});

				// Parse the response
				const jsonResponse: UiStringsMap = JSON.parse(responseText);
				
				// Translating the UxSchema
				// This function should throw error if there is any issue
				await this.translateSurveyUxSchema(survey, jsonResponse);

				success = true;
			} else {
				// Log the error with response
				TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateSurveyCdnClient.loadCdnStringResource.Request_Failed,
					{
						TimeMilliseconds: serviceCallTime,
						CampaignId: survey.getCampaignId(),
						SurveyId: survey.getSurveyInfo().getId(),
						Message: JSON.stringify({ 
							RequestUrl: requestUrl,
							HttpStatusCode: response.status,
							HttpStatusText: response.statusText,
							SimpleRequestEnabled: this.simpleRequestEnabled,
						}),
					});
			}
			// Done
		} catch (e) {
			TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateSurveyCdnClient.loadCdnStringResource.Exception,
				{
					ErrorMessage: e && e.toString(),
					CampaignId: survey.getCampaignId(),
					SurveyId: survey.getSurveyInfo().getId(),
					Message: JSON.stringify({ 
						RequestUrl: requestUrl,
						HttpStatusCode: response?.status,
						HttpStatusText: response?.statusText,
						SimpleRequestEnabled: this.simpleRequestEnabled,
					}),
				});
		}

		// In case of failure, we allow fallback if there is valid Content
		if (!success) {
			// Set the UxSchema to null so we fallback to Content
			survey.setUxSchema(null);
			if (survey.isContentValid()) {
				success = true;
			}
		}

		TelemetryLogger.getTelemetryLogger().log_Event(TelemetryEvent.FloodgateSurveyCdnClient.loadCdnStringResource.Completed,
			{
				CampaignId: survey.getCampaignId(),
				SurveyId: survey.getSurveyInfo().getId(),
				Message: JSON.stringify({
					Success: success,
					IsContentValid: survey.isContentValid(),
					IsUxSchemaPresent: !isNOU(survey.getUxSchema())
				}),
			});

		return success;
	}

	/**
	 * This function translates the given uxSchema in the parameter
	 * This is destructive function since it will create a new translated UxSchema and set the new one to given survey.
	 * However, the original UxSchema won't be changed and stay the same with orginal strings.
	 * Important note: the original UxSchema is shallow copied from Campaign Definition, so any change to it will affect the original
	 * Campaign Definition as well. Therefore, we should not changing the UxSchema if not needed.
	 * If there is issue in translation, it will throw Error
	 * @param survey survey with UxSchema to be translated
	 * @param uiStringMap dictionary contains locale strings
	*/
	public async translateSurveyUxSchema(survey: Api.ISurvey, uiStringMap: UiStringsMap): Promise<void> {
		const uxSchema = survey.getUxSchema();
		const promiseList = [];
		const errorList = [];

		// Using spread copy to create a new `uxSchema` and `pages` reference
		// then we can deep copy the inner fields as neccesary without changing the original object
		// Any other fields that we don't modify, they can be reused by spread copy
		const translatedResult: IUxSchema = {...uxSchema};
		translatedResult.pages = [...uxSchema.pages];

		// translate the pages
		for (let index = 0; index < uxSchema.pages.length; index++) {
			promiseList.push(
				this.translatePage(uiStringMap, uxSchema.pages[index], `page.${index}`, errorList)
				.then((result) => {
					translatedResult.pages[index] = result;
				})
			);
		}

		// translate the branching rule if possible
		if (uxSchema.branchingRules && uxSchema.branchingRules.length > 0) {
			const translatedRules = translatedResult.branchingRules = [];
			for (let indexRule = 0; indexRule < uxSchema.branchingRules.length; indexRule++) {
				const rule = uxSchema.branchingRules[indexRule];

				// Create deep copy the rule object so that we can translate the targets without changing the original rule
				const translatedRule: BranchingRule = {
					conditions: rule.conditions,
					targets: [...rule.targets]
				};
				translatedRules.push(translatedRule);

				// Translate the targets if possible
				if (rule.targets && rule.targets.length > 0) {
					for (let index = 0; index < rule.targets.length; index++) {
						const target = rule.targets[index];

						// translate the TargetOverride if possible
						if (target.type === BranchingTargetType.Override) {
							// Using spread copy to create a new `target` reference
							// then we can deep copy the inner fields as neccesary without changing the original object
							// Any other fields that we don't modify, they can be reused by spread copy
							const translatedTarget: BranchingTargetOverride = {...target};
							translatedRule.targets[index] = translatedTarget;
							const overrideTargetQuestion = uxSchema.pages[target.targetPageIndex].questions.find(question =>
								question.id === target.targetOverrideId);
							
							promiseList.push(
								this.translateQuestion(
									uiStringMap,
									target.overrideProperties,
									overrideTargetQuestion.type,
									`branchingRules.${indexRule}.targets.${index}`,
									errorList)
								.then((result) => {
									translatedTarget.overrideProperties = result as OverridePropertiesType;
								})
							);
						}
					}
				}
			}
		}

		await Promise.all(promiseList);

		if (errorList.length > 0) {
			throw new Error(JSON.stringify(errorList));
		}

		// Finally, set the new translated schema
		survey.setUxSchema(translatedResult);

		return;
	}

	/**
	 * Translate the given page, and putting error into errorList if there is any
	*/
	private translatePage(uiStringMap: UiStringsMap, schemaPage: IUxSchemaPage, pageKey: string, errorList: string[]): Promise<IUxSchemaPage> {
		const promiseList = [];
		const translatedResult: IUxSchemaPage = {...schemaPage};

		// Using spread copy to create a new `questions` reference
		// then we can deep copy the inner fields as neccesary without changing the original object
		// Any other fields that we don't modify, they can be reused by spread copy
		translatedResult.questions = [...schemaPage.questions];
		// title is optional
		if (schemaPage.title) {
			promiseList.push(
				this.translateString(uiStringMap, schemaPage.title, `${pageKey}.title`, errorList)
				.then((result) => {
					translatedResult['title'] = result;
				})
			);
		}

		for (let index = 0; index < schemaPage.questions.length; index++) {
			promiseList.push(
				this.translateQuestion(uiStringMap, schemaPage.questions[index], schemaPage.questions[index].type, `${pageKey}.questions.${index}`, errorList)
				.then((result) => {
					translatedResult.questions[index] = result as Question;
				})
			);
		}
		return Promise.all(promiseList).then(() => translatedResult);
	}

	/**
	 * Translate the given string, and putting error into errorList if there is any
	*/
	private translateQuestion(
		uiStringMap: UiStringsMap,
		translateObject: Question | OverridePropertiesType,
		questionType: QuestionType,
		questionKey: string,
		errorList: string[]
		): Promise<Question | OverridePropertiesType> {
		const promiseList = [];

		// Using spread copy to create a new object reference
		// then we can deep copy the inner object fields as neccesary without changing the original object
		// Any other fields that we don't modify, they can be reused by spread copy
		const translatedResult = {...translateObject};
		if (Object.prototype.hasOwnProperty.call(translateObject, "questionLabel")) {
			promiseList.push(
				this.translateString(uiStringMap, translateObject.questionLabel as string, `${questionKey}.questionLabel`, errorList)
				.then((result) => {
					translatedResult['questionLabel'] = result;
				})
			);
		}

		switch (questionType) {
			case QuestionType.MultipleChoice: 
			case QuestionType.SingleSelect: {
				if (Object.prototype.hasOwnProperty.call(translateObject, "availableOptions")) {
					promiseList.push(
						this.translateStringArray(uiStringMap, translateObject["availableOptions"] as string[],`${questionKey}.availableOptions`, errorList)
						.then((result) => {
							translatedResult['availableOptions'] = result;
						})
					);
				}
		
				break;
			}
			case QuestionType.Rating: {
				if (Object.prototype.hasOwnProperty.call(translateObject, "ratingValues")) {
					promiseList.push(
						this.translateStringArray(uiStringMap, translateObject["ratingValues"] as string[], `${questionKey}.ratingValues`, errorList)
						.then((result) => {
							translatedResult['ratingValues'] = result;
						})
					);
				}

				if (Object.prototype.hasOwnProperty.call(translateObject, "leftLegendValue")) {
					promiseList.push(
						this.translateString(uiStringMap, translateObject["leftLegendValue"] as string, `${questionKey}.leftLegendValue`, errorList)
						.then((result) => {
							translatedResult["leftLegendValue"] = result;
						})
					);
				}
					
				if (Object.prototype.hasOwnProperty.call(translateObject, "rightLegendValue")) {
					promiseList.push(
						this.translateString(uiStringMap, translateObject["rightLegendValue"] as string, `${questionKey}.rightLegendValue`, errorList)
						.then((result) => {
							translatedResult["rightLegendValue"] = result;
						})
					);
				}

				break;
			}
			case QuestionType.Likert: {
				if (Object.prototype.hasOwnProperty.call(translateObject, "verticalOptions")) {
					promiseList.push(
						this.translateStringArray(uiStringMap, translateObject["verticalOptions"] as string[], `${questionKey}.verticalOptions`, errorList)
						.then((result) => {
							translatedResult['verticalOptions'] = result;
						})
					);
				}
					
				if (Object.prototype.hasOwnProperty.call(translateObject, "horizontalStatements")) {
					promiseList.push(
						this.translateStringArray(
							uiStringMap,
							translateObject["horizontalStatements"] as string[],
							`${questionKey}.horizontalStatements`,
							errorList)
						.then((result) => {
							translatedResult['horizontalStatements'] = result;
						}));
				}

				break;
			}
			case QuestionType.Comment:
			case QuestionType.Label:
				// Nothing special for Comment, Label question
				break;
			default:
				// unknown type
				errorList.push(`${questionKey}.questionType is unknown.`);
				break;
		}
		return Promise.all(promiseList).then(() => translatedResult);
	}

	/**
	 * Translate the given string array, and putting error into errorList if there is any
	*/
	private translateStringArray(uiStringMap: UiStringsMap, stringArray: string[], keyArray: string, errorList: string[]): Promise<string[]> {
		const promiseList: Promise<string>[] = [];
		for (let index = 0; index < stringArray.length; index++) {
			promiseList.push(
				this.translateString(uiStringMap, stringArray[index], `${keyArray}.${index}`, errorList)
			);
		}
		return Promise.all(promiseList).then((result)=>result);
	}

	/**
	 * Translate the given string, and putting error into errorList if there is any
	 * @returns empty string if failure. Otherwise, return locale string on success.
	*/
	private translateString(uiStringMap: UiStringsMap, translatedString: string, keyString: string, errorList: string[]): Promise<string> {
		// reject is not used here to prevent the Promise.all to fail fast 
		return new Promise((resolve, _reject) => {
			if (Object.prototype.hasOwnProperty.call(uiStringMap, translatedString)) {
				resolve(uiStringMap[translatedString]);
			}
			else {
				errorList.push(`'${keyString}: ${translatedString}' does not exist in the strings map.`);
				resolve('');
			}
		});
	}
}
